import { useState, useEffect, useRef } from 'react'
import { Html5Qrcode } from 'html5-qrcode'
import { Product } from '../../types'
import { localDb, formatCurrency, calculateSMPrice } from '../../lib/db'
import { useToast } from '../../contexts/ToastContext'
import { useAuth } from '../../contexts/AuthContext'
import { v4 as uuidv4 } from 'uuid'

interface InvoiceItem {
    id: string
    product?: Product
    name: string
    price: number
    quantity: number
}

export default function AdminPOS() {
    const { user } = useAuth()
    const { showToast } = useToast()
    const [isScanning, setIsScanning] = useState(false)
    const [invoiceItems, setInvoiceItems] = useState<InvoiceItem[]>([])
    const [searchQuery, setSearchQuery] = useState('')
    const [searchResults, setSearchResults] = useState<Product[]>([])
    const [showAddNewModal, setShowAddNewModal] = useState(false)
    const [newItemData, setNewItemData] = useState({ name: '', price: 0, barcode: '' })
    const scannerRef = useRef<Html5Qrcode | null>(null)
    const [showInvoice, setShowInvoice] = useState(false)
    const [lastOrder, setLastOrder] = useState<{ id: string; pickup_code: string } | null>(null)

    const total = invoiceItems.reduce((sum, item) => sum + item.price * item.quantity, 0)

    useEffect(() => {
        return () => {
            // Cleanup scanner
            if (scannerRef.current) {
                scannerRef.current.stop().catch(() => { })
            }
        }
    }, [])

    useEffect(() => {
        if (searchQuery.length >= 2) {
            const results = localDb.products.search(searchQuery)
            setSearchResults(results.slice(0, 5))
        } else {
            setSearchResults([])
        }
    }, [searchQuery])

    const startScanner = async () => {
        try {
            const html5Qrcode = new Html5Qrcode("qr-reader")
            scannerRef.current = html5Qrcode

            await html5Qrcode.start(
                { facingMode: "environment" },
                {
                    fps: 10,
                    qrbox: { width: 250, height: 250 }
                },
                (decodedText) => {
                    handleBarcodeScan(decodedText)
                    stopScanner()
                },
                () => { } // Ignore errors
            )
            setIsScanning(true)
        } catch (err) {
            console.error('Scanner error:', err)
            showToast('Could not start camera. Please check permissions.', 'error')
        }
    }

    const stopScanner = async () => {
        if (scannerRef.current) {
            try {
                await scannerRef.current.stop()
            } catch { }
        }
        setIsScanning(false)
    }

    const handleBarcodeScan = (barcode: string) => {
        const product = localDb.products.getByBarcode(barcode)

        if (product) {
            addItemToInvoice(product)
        } else {
            // Product not found, show add new modal
            setNewItemData({ name: '', price: 0, barcode })
            setShowAddNewModal(true)
            showToast('Product not found. Add as new item.', 'info')
        }
    }

    const addItemToInvoice = (product: Product) => {
        setInvoiceItems(prev => {
            const existing = prev.find(item => item.product?.id === product.id)
            if (existing) {
                return prev.map(item =>
                    item.product?.id === product.id
                        ? { ...item, quantity: item.quantity + 1 }
                        : item
                )
            }
            return [...prev, {
                id: uuidv4(),
                product,
                name: product.name,
                price: product.sm_price,
                quantity: 1
            }]
        })
        showToast(`Added: ${product.name}`, 'success')
        setSearchQuery('')
        setSearchResults([])
    }

    const addNewItem = () => {
        if (!newItemData.name || newItemData.price <= 0) {
            showToast('Please enter name and price', 'error')
            return
        }

        // Add to inventory first
        const mrp = newItemData.price
        const sm_price = calculateSMPrice(mrp)

        const newProduct = localDb.products.create({
            name: newItemData.name,
            description: '',
            category: 'Others',
            mrp,
            sm_price,
            stock: 1,
            barcode: newItemData.barcode || undefined,
            image_url: ''
        })

        // Add to invoice
        setInvoiceItems(prev => [...prev, {
            id: uuidv4(),
            product: newProduct,
            name: newProduct.name,
            price: newProduct.sm_price,
            quantity: 1
        }])

        showToast(`Added new item: ${newItemData.name}`, 'success')
        setShowAddNewModal(false)
        setNewItemData({ name: '', price: 0, barcode: '' })
    }

    const updateQuantity = (itemId: string, quantity: number) => {
        if (quantity <= 0) {
            setInvoiceItems(prev => prev.filter(item => item.id !== itemId))
        } else {
            setInvoiceItems(prev => prev.map(item =>
                item.id === itemId ? { ...item, quantity } : item
            ))
        }
    }

    const removeItem = (itemId: string) => {
        setInvoiceItems(prev => prev.filter(item => item.id !== itemId))
    }

    const generateInvoice = () => {
        if (invoiceItems.length === 0) {
            showToast('Add items to generate invoice', 'error')
            return
        }

        // Create order
        const order = localDb.orders.create({
            user_id: user?.id || 'pos-customer',
            items: invoiceItems.map(item => ({
                product_id: item.product?.id || 'custom',
                product_name: item.name,
                quantity: item.quantity,
                price: item.price
            })),
            total,
            s_coins_used: 0,
            payment_method: 'upi'
        })

        // Mark as completed (in-store payment)
        localDb.orders.updateStatus(order.id, 'completed')

        setLastOrder({ id: order.id, pickup_code: order.pickup_code })
        setShowInvoice(true)
        showToast('Invoice generated!', 'success')
    }

    const printInvoice = () => {
        window.print()
    }

    const newTransaction = () => {
        setInvoiceItems([])
        setShowInvoice(false)
        setLastOrder(null)
    }

    return (
        <div className="pos-page">
            <div className="admin-page-header">
                <div>
                    <h1 className="admin-page-title">Point of Sale</h1>
                    <p className="admin-page-subtitle">Quick billing for in-store customers</p>
                </div>
            </div>

            <div className="pos-layout">
                {/* Left: Item Entry */}
                <div className="pos-entry">
                    {/* Scanner Section */}
                    <div className="scanner-section">
                        <div className="scanner-header">
                            <h3>📷 Scan Barcode</h3>
                            {isScanning ? (
                                <button className="btn btn-secondary btn-sm" onClick={stopScanner}>
                                    Stop Camera
                                </button>
                            ) : (
                                <button className="btn btn-primary btn-sm" onClick={startScanner}>
                                    Start Camera
                                </button>
                            )}
                        </div>

                        <div id="qr-reader" className={`qr-reader ${isScanning ? 'active' : ''}`}>
                            {!isScanning && (
                                <div className="scanner-placeholder">
                                    <span className="scanner-icon">📷</span>
                                    <p>Click "Start Camera" to scan barcodes</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Search Section */}
                    <div className="search-section">
                        <h3>🔍 Search Product</h3>
                        <div className="search-box">
                            <input
                                type="text"
                                className="input"
                                placeholder="Search by name or barcode..."
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                            />
                            {searchResults.length > 0 && (
                                <div className="search-results">
                                    {searchResults.map(product => (
                                        <button
                                            key={product.id}
                                            className="search-result-item"
                                            onClick={() => addItemToInvoice(product)}
                                        >
                                            <span className="result-name">{product.name}</span>
                                            <span className="result-price">{formatCurrency(product.sm_price)}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <button
                            className="btn btn-secondary add-manual-btn"
                            onClick={() => setShowAddNewModal(true)}
                        >
                            + Add Custom Item
                        </button>
                    </div>
                </div>

                {/* Right: Invoice */}
                <div className="pos-invoice">
                    <div className="invoice-header">
                        <h3>🧾 Current Invoice</h3>
                        {invoiceItems.length > 0 && (
                            <button className="btn btn-ghost btn-sm" onClick={() => setInvoiceItems([])}>
                                Clear All
                            </button>
                        )}
                    </div>

                    {invoiceItems.length === 0 ? (
                        <div className="invoice-empty">
                            <span>🛒</span>
                            <p>Scan or search to add items</p>
                        </div>
                    ) : (
                        <>
                            <div className="invoice-items">
                                {invoiceItems.map(item => (
                                    <div key={item.id} className="invoice-item">
                                        <div className="item-info">
                                            <span className="item-name">{item.name}</span>
                                            <span className="item-price">{formatCurrency(item.price)}</span>
                                        </div>
                                        <div className="item-controls">
                                            <button onClick={() => updateQuantity(item.id, item.quantity - 1)}>−</button>
                                            <span>{item.quantity}</span>
                                            <button onClick={() => updateQuantity(item.id, item.quantity + 1)}>+</button>
                                            <button className="remove-btn" onClick={() => removeItem(item.id)}>×</button>
                                        </div>
                                        <div className="item-total">
                                            {formatCurrency(item.price * item.quantity)}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="invoice-summary">
                                <div className="summary-row">
                                    <span>Items:</span>
                                    <span>{invoiceItems.reduce((sum, item) => sum + item.quantity, 0)}</span>
                                </div>
                                <div className="summary-row total">
                                    <span>Total:</span>
                                    <span>{formatCurrency(total)}</span>
                                </div>
                            </div>

                            <button className="btn btn-primary btn-lg generate-btn" onClick={generateInvoice}>
                                Generate Invoice
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Add New Item Modal */}
            {showAddNewModal && (
                <div className="modal-backdrop" onClick={() => setShowAddNewModal(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Add New Item</h2>
                            <button className="modal-close" onClick={() => setShowAddNewModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <div className="input-group">
                                <label className="input-label">Item Name *</label>
                                <input
                                    type="text"
                                    className="input"
                                    value={newItemData.name}
                                    onChange={e => setNewItemData(prev => ({ ...prev, name: e.target.value }))}
                                    autoFocus
                                />
                            </div>
                            <div className="input-group">
                                <label className="input-label">Price (₹) *</label>
                                <input
                                    type="number"
                                    className="input"
                                    value={newItemData.price || ''}
                                    onChange={e => setNewItemData(prev => ({ ...prev, price: Number(e.target.value) }))}
                                    min="0"
                                />
                            </div>
                            <div className="input-group">
                                <label className="input-label">Barcode (optional)</label>
                                <input
                                    type="text"
                                    className="input"
                                    value={newItemData.barcode}
                                    onChange={e => setNewItemData(prev => ({ ...prev, barcode: e.target.value }))}
                                />
                            </div>
                            <p className="modal-note">
                                This item will be added to inventory automatically.
                            </p>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={() => setShowAddNewModal(false)}>
                                Cancel
                            </button>
                            <button className="btn btn-primary" onClick={addNewItem}>
                                Add to Invoice
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Invoice Preview Modal */}
            {showInvoice && lastOrder && (
                <div className="modal-backdrop" onClick={() => setShowInvoice(false)}>
                    <div className="modal invoice-modal" onClick={e => e.stopPropagation()}>
                        <div className="invoice-print-content">
                            <div className="invoice-store-header">
                                <h2>👗 SM Fashion</h2>
                                <p>Nalgonda</p>
                            </div>

                            <div className="invoice-details">
                                <p>Invoice: #{lastOrder.id.slice(0, 8).toUpperCase()}</p>
                                <p>Date: {new Date().toLocaleString('en-IN')}</p>
                            </div>

                            <table className="invoice-table">
                                <thead>
                                    <tr>
                                        <th>Item</th>
                                        <th>Qty</th>
                                        <th>Price</th>
                                        <th>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoiceItems.map(item => (
                                        <tr key={item.id}>
                                            <td>{item.name}</td>
                                            <td>{item.quantity}</td>
                                            <td>{formatCurrency(item.price)}</td>
                                            <td>{formatCurrency(item.price * item.quantity)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            <div className="invoice-total">
                                <span>Total:</span>
                                <span>{formatCurrency(total)}</span>
                            </div>

                            <div className="invoice-footer">
                                <p>Thank you for shopping with us!</p>
                                <p className="pickup-code-print">Pickup Code: {lastOrder.pickup_code}</p>
                            </div>
                        </div>

                        <div className="invoice-actions no-print">
                            <button className="btn btn-secondary" onClick={printInvoice}>
                                🖨️ Print Invoice
                            </button>
                            <button className="btn btn-primary" onClick={newTransaction}>
                                New Transaction
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
        .pos-page {
          height: calc(100vh - 200px);
        }
        
        .pos-layout {
          display: grid;
          grid-template-columns: 1fr 400px;
          gap: var(--space-6);
          height: 100%;
        }
        
        .pos-entry {
          display: flex;
          flex-direction: column;
          gap: var(--space-6);
        }
        
        .scanner-section,
        .search-section {
          background: var(--bg-card);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-xl);
          padding: var(--space-4);
        }
        
        .scanner-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: var(--space-4);
        }
        
        .scanner-header h3,
        .search-section h3 {
          font-size: var(--text-base);
        }
        
        .qr-reader {
          background: var(--bg-tertiary);
          border-radius: var(--radius-lg);
          overflow: hidden;
          min-height: 200px;
        }
        
        .qr-reader.active {
          min-height: 300px;
        }
        
        .scanner-placeholder {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 200px;
          color: var(--text-muted);
        }
        
        .scanner-icon {
          font-size: 3rem;
          margin-bottom: var(--space-2);
        }
        
        .search-section h3 {
          margin-bottom: var(--space-4);
        }
        
        .search-box {
          position: relative;
          margin-bottom: var(--space-4);
        }
        
        .search-results {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-lg);
          margin-top: var(--space-1);
          overflow: hidden;
          z-index: 10;
        }
        
        .search-result-item {
          display: flex;
          justify-content: space-between;
          width: 100%;
          padding: var(--space-3);
          background: none;
          border: none;
          border-bottom: 1px solid var(--border-color);
          color: var(--text-primary);
          cursor: pointer;
          text-align: left;
        }
        
        .search-result-item:last-child {
          border-bottom: none;
        }
        
        .search-result-item:hover {
          background: var(--bg-glass);
        }
        
        .result-price {
          color: var(--color-success);
          font-weight: 600;
        }
        
        .add-manual-btn {
          width: 100%;
        }
        
        /* Invoice */
        .pos-invoice {
          background: var(--bg-card);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-xl);
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        
        .invoice-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: var(--space-4);
          border-bottom: 1px solid var(--border-color);
        }
        
        .invoice-header h3 {
          font-size: var(--text-base);
        }
        
        .invoice-empty {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: var(--text-muted);
        }
        
        .invoice-empty span {
          font-size: 3rem;
          margin-bottom: var(--space-2);
        }
        
        .invoice-items {
          flex: 1;
          overflow-y: auto;
          padding: var(--space-4);
        }
        
        .invoice-item {
          display: grid;
          grid-template-columns: 1fr auto auto;
          gap: var(--space-3);
          align-items: center;
          padding: var(--space-3);
          background: var(--bg-glass);
          border-radius: var(--radius-md);
          margin-bottom: var(--space-2);
        }
        
        .item-name {
          font-weight: 500;
          display: block;
        }
        
        .item-price {
          font-size: var(--text-sm);
          color: var(--text-muted);
        }
        
        .item-controls {
          display: flex;
          align-items: center;
          gap: var(--space-1);
        }
        
        .item-controls button {
          width: 28px;
          height: 28px;
          background: var(--bg-tertiary);
          border: none;
          border-radius: var(--radius-sm);
          color: var(--text-primary);
          font-weight: 600;
          cursor: pointer;
        }
        
        .item-controls button:hover {
          background: var(--bg-secondary);
        }
        
        .item-controls .remove-btn {
          background: rgba(239, 68, 68, 0.1);
          color: var(--color-error);
        }
        
        .item-total {
          font-weight: 600;
          min-width: 80px;
          text-align: right;
        }
        
        .invoice-summary {
          padding: var(--space-4);
          border-top: 1px solid var(--border-color);
        }
        
        .summary-row {
          display: flex;
          justify-content: space-between;
          padding: var(--space-2) 0;
        }
        
        .summary-row.total {
          font-size: var(--text-xl);
          font-weight: 700;
          padding-top: var(--space-4);
          border-top: 1px solid var(--border-color);
        }
        
        .generate-btn {
          margin: var(--space-4);
        }
        
        /* Invoice Modal */
        .invoice-modal {
          max-width: 400px;
        }
        
        .invoice-print-content {
          padding: var(--space-6);
        }
        
        .invoice-store-header {
          text-align: center;
          margin-bottom: var(--space-4);
        }
        
        .invoice-store-header h2 {
          font-size: var(--text-xl);
        }
        
        .invoice-store-header p {
          color: var(--text-muted);
        }
        
        .invoice-details {
          text-align: center;
          font-size: var(--text-sm);
          color: var(--text-muted);
          margin-bottom: var(--space-4);
        }
        
        .invoice-table {
          width: 100%;
          font-size: var(--text-sm);
          margin-bottom: var(--space-4);
        }
        
        .invoice-table th,
        .invoice-table td {
          padding: var(--space-2);
          text-align: left;
          border-bottom: 1px solid var(--border-color);
        }
        
        .invoice-total {
          display: flex;
          justify-content: space-between;
          font-size: var(--text-xl);
          font-weight: 700;
          padding: var(--space-4) 0;
          border-top: 2px solid var(--border-color);
        }
        
        .invoice-footer {
          text-align: center;
          padding-top: var(--space-4);
          border-top: 1px dashed var(--border-color);
        }
        
        .invoice-footer p {
          color: var(--text-muted);
          font-size: var(--text-sm);
        }
        
        .pickup-code-print {
          font-family: var(--font-display);
          font-size: var(--text-lg) !important;
          font-weight: 700;
          color: var(--text-primary) !important;
          margin-top: var(--space-2);
        }
        
        .invoice-actions {
          display: flex;
          gap: var(--space-3);
          padding: var(--space-4);
          border-top: 1px solid var(--border-color);
        }
        
        .invoice-actions .btn {
          flex: 1;
        }
        
        .modal-note {
          font-size: var(--text-sm);
          color: var(--text-muted);
          margin-top: var(--space-4);
        }
        
        .modal-close {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          color: var(--text-muted);
          background: none;
          border: none;
          cursor: pointer;
        }
        
        @media print {
          .no-print {
            display: none !important;
          }
          
          .modal-backdrop {
            background: white;
          }
          
          .invoice-modal {
            box-shadow: none;
            border: none;
          }
        }
        
        @media (max-width: 1024px) {
          .pos-layout {
            grid-template-columns: 1fr;
          }
          
          .pos-invoice {
            height: auto;
            min-height: 400px;
          }
        }
      `}</style>
        </div>
    )
}
